/**
 * Document page (mockup v7): header with number and status, pending-change
 * banners, the in-app PDF viewer, and the side panel (Details · Approval ·
 * Revisions · Activity). Approve / request changes / self-approve / submit /
 * withdraw all happen here (ADR-0009, ADR-0010, ADR-0011).
 */
import { useCallback, useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ApiError, api, type AckPerson, type DocDetail, type DocNote, type Role, type Suggestion, type VersionRow } from "../lib/api";
import { useStore } from "../lib/store";
import { AREA_LABELS, TYPE_LABELS, TYPE_NAMES, CLASSIFICATION_CODES, CLASSIFICATION_LABELS, RETENTION_CODES, RETENTION_LABELS, VISIBILITIES, VISIBILITY_LABELS,
  RESTRICTED, BUMPS, BUMP_LABELS, fmtVersion, nextVersion, coColor, coName, isVideoFile, type Bump } from "../lib/brand";
import { bytes, daysUntil, fmt, fmtDT, parseTs, saveBlob } from "../lib/format";
import { PEND_LABEL, pendKey } from "../lib/access";
import { PdfViewer } from "../components/PdfViewer";
import { VideoViewer } from "../components/VideoViewer";
import { FrameworkPicker, FrameworkTags } from "../components/FrameworkPicker";
import { Co, Empty, Loading, TypeBadge } from "../components/bits";
import { Icon } from "../components/Icon";

type PTab = "details" | "approval" | "notes" | "acks" | "suggestions" | "revisions" | "activity";

export function DocumentPage() {
  const { id = "" } = useParams();
  const { me, p, reload, toast, pushRecent } = useStore();
  const nav = useNavigate();
  const [data, setData] = useState<DocDetail | null>(null);
  const [err, setErr] = useState<ApiError | null>(null);
  const [vsel, setVsel] = useState<"current" | "pending">("current");
  const [ptab, setPtab] = useState<PTab | null>(null);
  const [archiving, setArchiving] = useState<null | "archive" | "retire">(null);
  const [busy, setBusy] = useState(false);
  // Suggest a change (approved mockup 2026-10-26): any reader of a published document.
  const [sp] = useSearchParams();
  const [suggesting, setSuggesting] = useState(false);
  const [sugs, setSugs] = useState<{ suggestions: Suggestion[]; can_decide: boolean } | null>(null);
  const loadSugs = useCallback(() => api.suggestions(id).then(setSugs).catch(() => setSugs({ suggestions: [], can_decide: false })), [id]);
  useEffect(() => { setSugs(null); setSuggesting(false); void loadSugs(); }, [loadSugs]);
  useEffect(() => { if (sp.get("tab") === "suggestions") setPtab("suggestions"); }, [id]);

  const load = useCallback(async () => {
    try { setData(await api.document(id)); setErr(null); }
    catch (e) { setErr(e as ApiError); }
  }, [id]);

  useEffect(() => { setData(null); setErr(null); setPtab(null); setVsel("current"); void load(); }, [load]);
  useEffect(() => { if (data) pushRecent(data.document.id); }, [data?.document.id, pushRecent]); // eslint-disable-line react-hooks/exhaustive-deps

  const d = data?.document;
  const cur = data?.versions.find((v) => v.state === "approved" && v.revision === d?.revision) ?? null;
  const pendV = d?.pending ? data?.versions.find((v) => v.id === d.pending!.version_id) ?? null : null;
  const showingPending = (vsel === "pending" || !cur) && !!pendV;
  const shown: VersionRow | null = showingPending ? pendV : cur;

  // Poll while the shown version's PDF copy is being made.
  useEffect(() => {
    if (shown?.rendition_status !== "pending") return;
    const t = setInterval(() => void load(), 6000);
    return () => clearInterval(t);
  }, [shown?.rendition_status, load]);

  if (err) {
    return (
      <div className="card empty view">
        <h3>Not found</h3>
        {err.status === 404 ? "That document doesn't exist, or you don't have access to it." : err.message}
        <div style={{ marginTop: 14 }}><button className="btn btn-ghost" onClick={() => nav("/library")}>Back to library</button></div>
      </div>
    );
  }
  if (!data || !d) return <Loading label="Opening document…" />;

  const perm = data.permissions;
  const pend = d.pending;
  const k = pendKey(d);
  const tab: PTab = ptab ?? (pend && (perm.can_approve || perm.can_review || perm.is_uploader) ? "approval" : "details");
  const after = async (msg: string) => { toast(msg); await Promise.all([load(), reload()]); };

  const act = async (fn: () => Promise<unknown>, msg: string) => {
    setBusy(true);
    try { await fn(); await after(msg); } catch (e) { toast((e as Error).message); } finally { setBusy(false); }
  };
  const co = d.checked_out;
  let banner: ReactNode = null;
  const sch = d.scheduled;
  if (d.archived && d.status === "retired") {
    banner = (
      <div className="banner archived"><span className="ic"><Icon name="archive" size={15} /></span>
        <div><b>Retired</b> {fmt(d.archived.at)} by {d.archived.by}. This document is at the end of its life: read-only and hidden from readers. DocVault keeps it and its history; it doesn't delete anything automatically. It can't be restored.
          {d.archived.reason && <q>{d.archived.reason}</q>}</div>
      </div>
    );
  } else if (d.archived) {
    banner = (
      <div className="banner archived"><span className="ic"><Icon name="archive" size={15} /></span>
        <div><b>Archived</b> {fmt(d.archived.at)} by {d.archived.by}. Readers can't see it and review reminders have stopped; authors, reviewers, approvers and admins still see it and its history.
          {d.archived.reason && <q>{d.archived.reason}</q>}</div>
        {perm.can_retire && <button className="btn btn-ghost btn-sm" disabled={busy} onClick={() => setArchiving("retire")}>Retire</button>}
        {perm.can_archive && <button className="btn btn-accent btn-sm" disabled={busy} onClick={() => act(() => api.restore(d.id), `Restored ${d.base_number}.`)}>Restore</button>}
      </div>
    );
  } else if (sch) {
    banner = (
      <div className="banner rev"><span className="ic"><Icon name="cal" size={15} /></span>
        <div><b>v{sch.version} is approved and takes effect on {fmt(sch.effective_on)}.</b> {d.version ? <>Until then v{d.version} stays the current version for everyone.</> : <>Readers will see it from that date.</>}</div>
      </div>
    );
  } else if (co && !perm.checked_out_by_me) {
    banner = (
      <div className="banner draft"><span className="ic"><Icon name="lock" size={15} /></span>
        <div><b>Checked out by {co.by.name}</b> since {fmtDT(co.at)}. They're preparing the next revision; nobody else can start one until it's back.</div>
        {perm.can_cancel_checkout && <button className="btn btn-ghost btn-sm" disabled={busy}
          onClick={() => { if (confirm(`Release ${co.by.name}'s check-out? They'll need to check it out again to upload a revision.`)) void act(() => api.cancelCheckout(d.id), "Check-out released."); }}>Release</button>}
      </div>
    );
  } else if (co && perm.checked_out_by_me) {
    banner = (
      <div className="banner info"><span className="ic"><Icon name="lock" size={15} /></span>
        <div><b>You have this document checked out</b> since {fmtDT(co.at)}. Upload the new revision when it's ready; that checks it back in.</div>
        <button className="btn btn-accent btn-sm" onClick={() => nav(`/new/${d.id}`)}>Upload new revision</button>
        <button className="btn btn-ghost btn-sm" disabled={busy} onClick={() => act(() => api.cancelCheckout(d.id), "Check-out canceled.")}>Cancel check-out</button>
      </div>
    );
  } else if (d.update_in_progress && !pend) {
    banner = <div className="banner info"><span className="ic">i</span><div><b>An update to this document is in progress.</b> You're viewing v{d.version}, the current version. You'll see the new revision here once it's approved.</div></div>;
  } else if (k === "chg" && pend) {
    banner = (
      <div className="banner chg"><span className="ic">!</span>
        <div><b>Changes requested on v{pend.proposed_version}</b>. It's back with {pend.by.name}{d.version ? `; v${d.version} stays the current version` : ""}.
          {pend.last_comment && <q>{pend.last_comment}</q>}</div>
        {perm.is_uploader ? <button className="btn btn-accent btn-sm" onClick={() => nav(`/new/${d.id}`)}>Upload fixed version</button>
          : d.revision ? <button className="btn btn-ghost btn-sm" onClick={() => setVsel("pending")}>View v{pend.proposed_version} draft</button> : null}
      </div>
    );
  } else if (k === "rev" && pend && d.revision) {
    banner = <div className="banner rev"><span className="ic"><Icon name="clock" size={16} /></span><div><b>v{pend.proposed_version} ({pend.bump ?? "minor"} change) is waiting for approval</b>, submitted by {pend.by.name} on {fmtDT(pend.submitted_at)}. v{d.version} stays current until it's approved.</div></div>;
  } else if (k === "draft" && pend && d.revision) {
    banner = <div className="banner draft"><span className="ic"><Icon name="pen" size={15} /></span><div><b>The next version is being drafted</b> by {pend.by.name}. It hasn't been submitted yet.</div></div>;
  }

  const [stCls, stLab] = d.status === "retired" ? ["arch", "Retired"] : d.archived ? ["arch", "Archived"] : d.revision ? ["pub", "Published"]
    : sch ? ["info", "Approved"] : k ? [k, PEND_LABEL[k]] : ["draft", "Draft"];
  const canSubmit = !d.archived && !!pend && perm.is_uploader && pend.state === "draft" && !!pendV?.upload_confirmed_at && !pend.on_hold;

  return (
    <div className="view">
      <div className="dochead">
        <div>
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <span className="badge-no"><i style={{ background: coColor(d.company) }} />{d.base_number}</span>
            {d.version ? <span className="badge-ver">v{d.version}</span> : <span className="badge-ver muted">Not yet released</span>}
            <span className={`pill ${stCls}`}>{stLab}</span>
            {d.classification !== "INT" && <span className={`cls cls-${d.classification}`}>{CLASSIFICATION_LABELS[d.classification] ?? d.classification}</span>}
            {co && <span className="pill lock" title={`Since ${fmtDT(co.at)}`}>Checked out · {perm.checked_out_by_me ? "you" : co.by.name}</span>}
            {d.requires_ack && d.acknowledged === true && <span className="pill pub" title="You confirmed you've read this version">Acknowledged</span>}
            {pend?.on_hold && <button type="button" className="pill hold" onClick={() => setPtab("notes")} title="See why in Notes">On hold</button>}
            {k && d.revision && pend ? <span className={`pill ${k}`}>v{pend.proposed_version} · {PEND_LABEL[k]}</span> : d.update_in_progress && !pend ? <span className="pill info">Update in progress</span> : null}
          </div>
          <div style={{ display: "flex", gap: 12, alignItems: "center", marginTop: 8 }}><TypeBadge type={d.type} /><h1 style={{ margin: 0 }}>{d.title}</h1></div>
          {d.description && <div className="lede">{d.description}</div>}
        </div>
        <div className="row-btns">
          {perm.can_edit_online && <button className="btn btn-ghost" title="Edit the Word, Excel or PowerPoint file here; saves go to your draft"
            onClick={() => {
              if (pend) return nav(`/doc/${d.id}/edit`);
              const notes = window.prompt("What are you changing? (goes in the revision history)")?.trim();
              if (notes) nav(`/doc/${d.id}/edit?notes=${encodeURIComponent(notes)}`);
            }}><Icon name="pen" />Edit in browser</button>}
          {d.version && !d.archived && d.status === "published" && <button className="btn btn-ghost" onClick={() => setSuggesting((v) => !v)} title="Tell the owner something should change">Suggest a change</button>}
          {perm.can_checkout && !d.archived && <button className="btn btn-ghost" disabled={busy} title="Lock it while you prepare the next revision"
            onClick={() => act(() => api.checkout(d.id), `Checked out ${d.base_number}. Upload the new revision when it's ready.`)}><Icon name="lock" />Check out</button>}
          {canSubmit && !d.version && <SubmitButton vid={pend!.version_id} onDone={() => after(`Submitted ${d.base_number} v1.0.0 for approval.`)} />}
          {canSubmit && d.version && <button className="btn btn-accent" onClick={() => setPtab("approval")}>Submit for approval</button>}
          {perm.can_archive && !d.archived && <button className="btn btn-ghost" onClick={() => setArchiving((v) => (v === "archive" ? null : "archive"))}><Icon name="archive" />Archive</button>}
          {perm.can_retire && !d.archived && <button className="btn btn-ghost" onClick={() => setArchiving((v) => (v === "retire" ? null : "retire"))}>Retire</button>}
          {(perm.can_approve || perm.can_review) && !d.archived && <button className="btn btn-accent" onClick={() => { setPtab("approval"); setTimeout(() => document.getElementById("cmt")?.focus(), 50); }}>{perm.can_review ? "Review" : "Review & approve"}</button>}
        </div>
      </div>
      {banner}
      {d.requires_ack && d.version && d.acknowledged === false && !d.archived && <AckBox d={d} onDone={() => after(`Thanks. You acknowledged ${d.base_number} v${d.version}.`)} />}
      {suggesting && <SuggestForm d={d} onDone={async (msg) => { setSuggesting(false); if (msg) { toast(msg); await loadSugs(); } }} />}
      {archiving === "archive" && !d.archived && (
        <div className="card archive-box">
          <h3>Archive {d.base_number}?</h3>
          <p>Readers stop seeing it and review reminders stop. Authors, reviewers, approvers and admins can still find it under <b>Show archived</b> in the library, with its full history, and an admin can restore it.
            {pend?.state === "in_review" && <><br /><b>A revision is waiting for approval.</b> Approve it, request changes, or have the author withdraw it first.</>}</p>
          <ArchiveForm busy={busy} disabled={pend?.state === "in_review" || !!sch} onCancel={() => setArchiving(null)}
            onArchive={(reason) => act(async () => { await api.archive(d.id, reason); setArchiving(null); }, `Archived ${d.base_number}.`)} />
        </div>
      )}
      {archiving === "retire" && d.status !== "retired" && (
        <div className="card archive-box">
          <h3>Retire {d.base_number}?</h3>
          <p><b>This can't be undone.</b> The document becomes read-only for good: hidden from readers, no new versions. DocVault keeps it and its history; its retention rule{d.retention ? ` (${RETENTION_LABELS[d.retention] ?? d.retention})` : ""} is a label for your records, and nothing is deleted automatically. Use Archive instead if you might bring it back.</p>
          <ArchiveForm busy={busy} disabled={false} action="Retire" onCancel={() => setArchiving(null)}
            onArchive={(reason) => act(async () => { await api.retire(d.id, reason); setArchiving(null); }, `Retired ${d.base_number}.`)} />
        </div>
      )}
      <div className="docgrid">
        <Viewer d={data} cur={cur} pendV={pendV} showingPending={showingPending} setVsel={setVsel} meName={me.user.display_name} onRetried={load} />
        <aside className="card side-panel">
          <div className={`ptabs ${(perm.can_see_pending && d.requires_ack && d.version) || (sugs && (sugs.can_decide || sugs.suggestions.length)) ? "many" : ""}`} role="tablist">
            {(["details", "approval", ...(perm.can_see_pending ? ["notes"] : []), ...(perm.can_see_pending && d.requires_ack && d.version ? ["acks"] : []), ...(sugs && (sugs.can_decide || sugs.suggestions.length) ? ["suggestions"] : []), "revisions", "activity"] as PTab[]).map((t) => (
              <button key={t} className={`tab ${tab === t ? "on" : ""}`} role="tab" aria-selected={tab === t} onClick={() => setPtab(t)}>
                {{ details: "Details", approval: "Approval", notes: "Notes", acks: "Read by", suggestions: "Suggestions", revisions: "Revisions", activity: "Activity" }[t]}
                {t === "suggestions" && sugs && sugs.suggestions.filter((x) => x.status === "open").length > 0 && <span className="n">{sugs.suggestions.filter((x) => x.status === "open").length}</span>}
                {t === "notes" && pend?.on_hold && <span className="n" style={{ background: "#ECE7F8", color: "#5D3F8F" }}>!</span>}
                {t === "approval" && pend?.state === "in_review" && <span className="n" style={{ background: "var(--warn-bg)", color: "var(--warn)" }}>1</span>}
              </button>
            ))}
          </div>
          <div className="pbody">
            {tab === "details" && <DetailsTab key={data.document.id + (data.document.frameworks ?? []).join("|")} data={data} canOriginalRole={perm.can_see_pending} onSaved={async () => { await Promise.all([load(), reload()]); }} />}
            {tab === "approval" && <ApprovalTab data={data} pendV={pendV} after={after} goEdit={() => nav(`/new/${d.id}`)} />}
            {tab === "notes" && <NotesTab data={data} canHold={perm.can_author} after={async () => { await Promise.all([load(), reload()]); }} />}
            {tab === "acks" && <AcksTab data={data} />}
            {tab === "suggestions" && sugs && <SuggestionsTab data={data} sugs={sugs} reload={loadSugs} />}
            {tab === "revisions" && <RevisionsTab data={data} />}
            {tab === "activity" && <ActivityTab data={data} />}
          </div>
        </aside>
      </div>
      {!p.canSeePending(d.company) && <div className="foot-note">Readers see the approved PDF copy. Every view and download is recorded.</div>}
    </div>
  );
}

// ---------------------------------------------------------------- read acknowledgments (ADR-0020)
function AckBox({ d, onDone }: { d: DocDetail["document"]; onDone: () => Promise<void> }) {
  const { toast } = useStore();
  const [read, setRead] = useState(false);
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try { await api.acknowledge(d.id); await onDone(); } catch (e) { toast((e as Error).message); } finally { setBusy(false); }
  };
  return (
    <div className="banner ack"><span className="ic"><Icon name="ok" size={15} /></span>
      <div><b>Please read v{d.version} and acknowledge it.</b> {(d.ack_roles ?? []).length ? "Your company asks people in your role to confirm they have." : "Your company asks everyone who reads this document to confirm they have."}
        <label className="ack-chk"><input type="checkbox" checked={read} onChange={(e) => setRead(e.target.checked)} />I have read and understood this document.</label></div>
      <button className={`btn btn-accent btn-sm ${busy ? "busy" : ""}`} disabled={!read || busy} onClick={() => void go()}>Acknowledge</button>
    </div>
  );
}

function AcksTab({ data }: { data: DocDetail }) {
  const [st, setSt] = useState<{ version: string | null; required: number; done: number; people: AckPerson[] } | null>(null);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => { api.acknowledgments(data.document.id).then(setSt, (e: Error) => setErr(e.message)); }, [data.document.id, data.document.version]);
  if (err) return <div className="note bad">{err}</div>;
  if (!st) return <Loading />;
  const pct = st.required ? Math.round((st.done / st.required) * 100) : 0;
  return (
    <>
      <div className="ack-sum"><b>{st.done} of {st.required}</b> have acknowledged v{st.version}<span className="cap"> · {pct}%</span></div>
      <div className="ack-bar"><i style={{ width: `${pct}%` }} /></div>
      <ul className="ack-list">
        {st.people.map((x) => (
          <li key={x.id}><span><b>{x.name}</b><small>{x.email}</small></span>
            {x.acknowledged_at ? <span className="pill pub sm">{fmt(x.acknowledged_at)}{x.version ? ` · v${x.version}` : ""}</span>
              : x.due_on ? <span className={`pill sm ${x.due_on < new Date().toISOString().slice(0, 10) ? "bad" : "rev"}`} title="Joined after this version came into force">New · due {fmt(x.due_on)}</span>
              : <span className="pill draft sm">Not yet</span>}</li>
        ))}
      </ul>
      <div className="row-btns" style={{ marginTop: 8 }}><button className="btn btn-ghost btn-sm" onClick={() => exportAcks(data, st)}>Export for an audit</button></div>
      <div className="help">{(data.document.ack_roles ?? []).length ? `People in ${(data.document.ack_roles ?? []).map((r) => ROLE_LABEL[r] ?? r).join(", ")} are asked.` : "Everyone who can read it is asked."} {data.document.ack_each_version === false ? "One acknowledgment counts for every version." : "A new major or minor version asks again; a revision fix doesn't."} People still waiting get a reminder after 3 days, then weekly.</div>
    </>
  );
}

const ROLE_LABEL: Record<string, string> = { reader: "Readers", author: "Authors", reviewer: "Reviewers", approver: "Approvers", admin: "Admins",
  clienthub_viewer: "TechHub viewers", clienthub_editor: "TechHub editors", clienthub_accounting: "Accounting" };

/** Who must acknowledge it (approved mockup 2026-10-26): everyone who can read it, or chosen roles; ask again on each version. */
function AckAudience({ d, onSaved }: { d: DocDetail["document"]; onSaved: () => Promise<void> }) {
  const { toast } = useStore();
  const roles = (d.ack_roles ?? []) as Role[];
  const [some, setSome] = useState(roles.length > 0);
  const save = async (b: { ack_roles?: Role[]; ack_each_version?: boolean }, msg: string) => {
    try { await api.updateDoc(d.id, b); toast(msg); await onSaved(); } catch (e) { toast((e as Error).message); }
  };
  const toggle = (r: Role) => {
    const next = roles.includes(r) ? roles.filter((x) => x !== r) : [...roles, r];
    void save({ ack_roles: next }, next.length ? `Asked of: ${next.map((x) => ROLE_LABEL[x]).join(", ")}.` : "Asked of everyone who can read it.");
  };
  return (
    <div style={{ marginTop: 8 }}>
      <div className="lbl" style={{ marginBottom: 2 }}>Who must acknowledge it</div>
      <label className="rad"><input type="radio" checked={!some} onChange={() => { setSome(false); if (roles.length) void save({ ack_roles: [] }, "Asked of everyone who can read it."); }} /> Everyone who can read it</label>
      <label className="rad"><input type="radio" checked={some} onChange={() => setSome(true)} /> Only these roles</label>
      {some && <div className="br-chips" style={{ margin: "6px 0 0 24px" }}>
        {(Object.keys(ROLE_LABEL) as Role[]).map((r) => (
          <button key={r} type="button" className={`chip-btn ${roles.includes(r) ? "on" : ""}`} aria-pressed={roles.includes(r)} onClick={() => toggle(r)}>{roles.includes(r) ? "✓ " : ""}{ROLE_LABEL[r]}</button>))}
      </div>}
      <label className="rad" style={{ marginTop: 6 }}><input type="checkbox" checked={d.ack_each_version !== false}
        onChange={(e) => void save({ ack_each_version: e.target.checked }, e.target.checked ? "A new version asks again." : "One acknowledgment now counts for every version.")} /> Ask again on each new version</label>
      <div className="cap">New people in these roles are asked automatically, with 7 days to confirm.</div>
    </div>
  );
}

function exportAcks(data: DocDetail, st: { version: string | null; people: AckPerson[] }) {
  const q = (v: string) => `"${v.replace(/"/g, '""')}"`;
  const rows = [["Name", "Email", "Acknowledged", "Version", "Due"].join(","),
    ...st.people.map((x) => [q(x.name), q(x.email), x.acknowledged_at ? q(fmtDT(x.acknowledged_at)) : "", x.version ?? "", x.due_on ?? ""].join(","))];
  saveBlob(new Blob([`${data.document.base_number} v${st.version} acknowledgments, exported ${new Date().toISOString().slice(0, 10)}\n` + rows.join("\n")], { type: "text/csv" }),
    `${data.document.base_number}-acknowledgments.csv`);
}

// ---------------------------------------------------------------- suggest a change
function SuggestForm({ d, onDone }: { d: DocDetail["document"]; onDone: (msg?: string) => Promise<void> }) {
  const { toast } = useStore();
  const [section, setSection] = useState("");
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    try { await api.suggest(d.id, { body: body.trim(), section: section.trim() || undefined }); await onDone(`Sent to ${d.owner?.name ?? "the owner"}. You'll get an email when it's answered.`); }
    catch (e) { toast((e as Error).message); } finally { setBusy(false); }
  };
  return (
    <div className="card sect" style={{ marginBottom: 14, maxWidth: 720 }}>
      <h3>Suggest a change</h3>
      <label className="field"><span className="lbl">Where <span className="help-in">optional</span></span><input className="inp" value={section} maxLength={200} placeholder="e.g. Step 4, joining the domain" onChange={(e) => setSection(e.target.value)} /></label>
      <label className="field"><span className="lbl">What should change</span><textarea className="inp" rows={4} value={body} maxLength={4000} autoFocus onChange={(e) => setBody(e.target.value)} /></label>
      <div className="row-btns"><button className="btn btn-accent" disabled={busy || body.trim().length < 5} onClick={() => void send()}>Send to the owner</button><button className="btn btn-ghost" onClick={() => void onDone()}>Cancel</button></div>
      <div className="help">Goes to {d.owner?.name ?? "the document's owner"}. Accepted suggestions are attached to the next revision, so its reviewers see them.</div>
    </div>
  );
}

function SuggestionsTab({ data, sugs, reload }: { data: DocDetail; sugs: { suggestions: Suggestion[]; can_decide: boolean }; reload: () => Promise<unknown> }) {
  const { toast, reloadTasks } = useStore();
  const nav = useNavigate();
  const [declining, setDeclining] = useState<string | null>(null);
  const [note, setNote] = useState("");
  const decide = async (x: Suggestion, status: "accepted" | "declined") => {
    try {
      await api.decideSuggestion(x.id, { status, note: status === "declined" ? note.trim() : undefined });
      void reloadTasks();
      if (status === "accepted") {
        const notes = `From ${x.author_name}'s suggestion${x.section ? ` (${x.section})` : ""}: ${x.body}`.slice(0, 1000);
        const perm = data.permissions;
        // A new revision needs the document checked out (2026-10-30: this step was missing, so the
        // revision page refused the upload). Someone else holding it, or a revision already in
        // progress, means there's nothing to start here yet.
        const held = data.document.checked_out;
        if (data.document.pending || (held && !perm.checked_out_by_me) || (!held && !perm.can_checkout)) {
          toast(data.document.pending ? "Accepted. A revision is already in progress, so add the change there."
            : held ? `Accepted. ${held.by.name} has it checked out, so the change goes in their revision.`
            : "Accepted. An author starts the revision; the suggestion stays on the Suggestions tab.");
          await reload(); return;
        }
        if (!perm.checked_out_by_me) await api.checkout(data.document.id);
        toast("Accepted and checked out. Upload the revised file; the suggestion is in What changed.");
        return nav(`/new/${data.document.id}?notes=${encodeURIComponent(notes)}`);
      }
      toast("Declined. They'll get an email with your reason.");
      setDeclining(null); setNote(""); await reload();
    } catch (e) { toast((e as Error).message); }
  };
  if (!sugs.suggestions.length) return <div className="cap">No suggestions yet. Anyone who reads it can suggest a change.</div>;
  return (
    <ul className="sg-list">
      {sugs.suggestions.map((x) => (
        <li key={x.id} className={x.status === "open" ? "" : "dim"}>
          <div><b>{x.author_name}</b> <span className="cap">· {fmt(x.created_at)}{x.section ? ` · ${x.section}` : ""}</span>
            {x.status !== "open" && <span className={`pill sm ${x.status === "accepted" ? "pub" : "draft"}`} style={{ marginLeft: 6 }}>{x.status === "accepted" ? "Accepted" : "Declined"}</span>}</div>
          <div style={{ whiteSpace: "pre-wrap" }}>{x.body}</div>
          {x.decision_note && <div className="cap">"{x.decision_note}" · {x.decider_name}</div>}
          {x.status === "open" && sugs.can_decide && (declining === x.id ? (
            <div style={{ marginTop: 6 }}><textarea className="inp" rows={2} value={note} autoFocus placeholder="Why not? They'll see this." onChange={(e) => setNote(e.target.value)} />
              <div className="row-btns"><button className="btn btn-accent btn-sm" disabled={note.trim().length < 3} onClick={() => void decide(x, "declined")}>Decline</button><button className="btn btn-ghost btn-sm" onClick={() => setDeclining(null)}>Cancel</button></div></div>
          ) : (
            <div className="row-btns" style={{ marginTop: 6 }}><button className="btn btn-accent btn-sm" onClick={() => void decide(x, "accepted")}>Accept and start a revision</button><button className="btn btn-ghost btn-sm" onClick={() => { setDeclining(x.id); setNote(""); }}>Decline…</button></div>
          ))}
        </li>
      ))}
    </ul>
  );
}

// ---------------------------------------------------------------- viewer
function Viewer({ d, cur, pendV, showingPending, setVsel, meName, onRetried }: {
  d: DocDetail; cur: VersionRow | null; pendV: VersionRow | null; showingPending: boolean;
  setVsel: (v: "current" | "pending") => void; meName: string; onRetried: () => Promise<void>;
}) {
  const { toast } = useStore();
  const doc = d.document;
  const v = showingPending ? pendV : cur;
  const [url, setUrl] = useState<string | null>(null);
  const [state, setState] = useState<"loading" | "ready" | "not_ready" | "failed" | "no_file" | "error">("loading");
  const [msg, setMsg] = useState("");
  const vid = v?.id;
  const status = v?.rendition_status;
  const uploaded = !!v?.upload_confirmed_at || v?.state === "approved";
  const reqRef = useRef(0);
  const isVideo = !!v && isVideoFile(v.mime_type, v.file_name);

  useEffect(() => {
    const n = ++reqRef.current;
    setUrl(null);
    if (!vid) { setState("no_file"); return; }
    if (!uploaded) { setState("no_file"); return; }
    setState("loading");
    api.fileUrl(doc.id, { kind: "pdf", version: vid, inline: true }).then(
      (r) => { if (n === reqRef.current) { setUrl(r.url); setState("ready"); } },
      (e: ApiError) => {
        if (n !== reqRef.current) return;
        setMsg(e.message);
        setState(e.code === "pdf_not_ready" ? "not_ready" : e.code === "pdf_failed" ? "failed" : "error");
      }
    );
  }, [doc.id, vid, status, uploaded]);

  const download = async (kind: "pdf" | "original") => {
    if (!v) return;
    try {
      const r = await api.fileUrl(doc.id, { kind, version: v.id });
      window.location.assign(r.url);
    } catch (e) { toast((e as Error).message); }
  };
  const retry = async () => {
    if (!v) return;
    try { await api.retryPdf(v.id); toast("Making the PDF copy. It usually takes under a minute."); await onRetried(); }
    catch (e) { toast((e as Error).message); }
  };

  const draft = showingPending;
  const num = draft ? `${doc.base_number} · v${doc.pending?.proposed_version ?? "1.0.0"} DRAFT` : doc.number;
  const clsWord = doc.classification !== "INT" ? `${(CLASSIFICATION_LABELS[doc.classification] ?? doc.classification).toUpperCase()} · ` : "";
  const today = fmtDT(new Date().toISOString());
  const stamp = draft
    ? { watermark: "DRAFT", left: `${clsWord}DRAFT - NOT APPROVED · for review only`, right: `${num} · Viewed by ${meName} · ${today}`, draft: true }
    : { watermark: "CONTROLLED COPY", left: `${clsWord}Controlled copy · ${doc.number} · Uncontrolled when printed or downloaded`, right: `Viewed by ${meName} · ${today}`, draft: false };

  const canRetry = d.permissions.can_see_pending;
  let placeholder: ReactNode = null;
  if (state === "no_file") placeholder = (
    <div><b>No file yet</b>
      {d.permissions.is_uploader ? <>The upload didn't finish. Upload the file again to continue.<div className="row-btns"><Link className="btn btn-accent btn-sm" to={`/new/${doc.id}`}>Upload file</Link></div></> : "The author hasn't finished uploading this revision."}
    </div>
  );
  else if (state === "not_ready" && status !== "pending") placeholder = (
    // Uploaded before PDF copies were made on upload (2026-10-20), e.g. imported drafts.
    <div><b>No PDF copy yet</b>
      <div style={{ margin: "6px 0 0" }}>{canRetry ? "Make one to read this draft here, or open the original." : "It will appear here once it's been made."}</div>
      {canRetry && <div className="row-btns"><button className="btn btn-accent btn-sm" onClick={retry}>Make the PDF copy</button></div>}
      {d.permissions.can_download_original && <div className="row-btns"><button className="btn btn-ghost btn-sm" onClick={() => download("original")}><Icon name="dl" />Open the original instead</button></div>}
    </div>
  );
  else if (state === "not_ready") placeholder = (
    <div><b>Making the PDF copy…</b>
      <div className="spin" style={{ justifyContent: "center" }}>{status === "pending" ? "This usually takes under a minute. The page updates by itself." : msg}</div>
      {canRetry && status !== "pending" && <div className="row-btns"><button className="btn btn-ghost btn-sm" onClick={retry}>Make the PDF copy</button></div>}
      {d.permissions.can_download_original && <div className="row-btns"><button className="btn btn-ghost btn-sm" onClick={() => download("original")}><Icon name="dl" />Open the original instead</button></div>}
    </div>
  );
  else if (state === "failed") placeholder = (
    <div><b>The PDF copy couldn't be made</b>{msg}
      <div className="row-btns">
        {canRetry && <button className="btn btn-accent btn-sm" onClick={retry}>Try again</button>}
        {d.permissions.can_download_original && <button className="btn btn-ghost btn-sm" onClick={() => download("original")}><Icon name="dl" />Download original</button>}
      </div>
    </div>
  );
  else if (state === "error") placeholder = <div><b>The document couldn't be opened</b>{msg}</div>;

  const isFormForReader = !d.permissions.can_see_pending && doc.type === "FRM" && d.permissions.can_download_original;
  const tools = (
    <>
      {cur && pendV && (
        <>
          <span className="sep" />
          <div className="vs">
            <button className={!showingPending ? "on" : ""} onClick={() => setVsel("current")}>v{cur.version ?? cur.revision} current</button>
            <button className={showingPending ? "on" : ""} onClick={() => setVsel("pending")}>v{doc.pending?.proposed_version} {doc.pending?.state === "in_review" ? "in review" : "draft"}</button>
          </div>
        </>
      )}
      <span style={{ flex: 1 }} />
      {v && uploaded && !isVideo && <button className="ib" onClick={() => download("pdf")} disabled={state !== "ready"} title="Download the PDF copy (every page is marked with the number, who downloaded it and when)"><Icon name="dl" size={14} /> PDF</button>}
      {v && uploaded && d.permissions.can_see_pending && <button className="ib" onClick={() => download("original")} title={v.file_name}><Icon name="dl" size={14} /> Original</button>}
      {v && isFormForReader && <button className="ib on" onClick={() => download("original")} title={v.file_name}><Icon name="dl" size={14} /> Fillable form</button>}
    </>
  );

  if (isVideo) {
    const refresh = async () => (await api.fileUrl(doc.id, { kind: "pdf", version: vid, inline: true })).url;
    return <VideoViewer url={state === "ready" ? url : null} placeholder={state === "ready" || state === "loading" ? null : placeholder}
      stamp={stamp} tools={tools} onExpired={refresh} allowDownload={d.permissions.can_see_pending} />;
  }
  return <PdfViewer url={state === "ready" ? url : null} placeholder={placeholder} stamp={stamp} tools={tools} />;
}

// ---------------------------------------------------------------- side panel tabs
function DetailsTab({ data, canOriginalRole, onSaved }: { data: DocDetail; canOriginalRole: boolean; onSaved: () => Promise<void> }) {
  const { p, toast, me } = useStore();
  const d = data.document;
  const n = daysUntil(d.review_date);
  const canTag = data.permissions.can_author || p.adminCodes.includes(d.company);
  const [editing, setEditing] = useState(false);
  const [tags, setTags] = useState<string[]>(d.frameworks ?? []);
  const [saving, setSaving] = useState(false);
  const shown = data.versions.find((x) => x.state === "approved") ?? data.versions[0];
  const isVideo = !!shown && isVideoFile(shown.mime_type, shown.file_name);
  const saveTags = async () => {
    setSaving(true);
    try { await api.setFrameworks(d.id, tags); toast("Framework tags saved."); setEditing(false); await onSaved(); }
    catch (e) { toast((e as Error).message); }
    finally { setSaving(false); }
  };
  return (
    <>
      <dl className="kv">
        <dt>Document ID</dt><dd className="mono" style={{ fontSize: 12.5 }}>{d.base_number}
          {d.former_numbers.length > 0 && <div className="cap" title="Numbers this document had before the code lists changed">Formerly {d.former_numbers.join(", ")}</div>}</dd>
        <dt>Version</dt><dd>{d.version ? <span className="mono">{d.version}</span> : "Not yet released"}{d.scheduled && <div className="cap">v{d.scheduled.version} from {fmt(d.scheduled.effective_on)}</div>}</dd>
        <dt>Company</dt><dd><Co code={d.company} /></dd>
        <dt>Type</dt><dd>{TYPE_NAMES[d.type] ?? TYPE_LABELS[d.type] ?? d.type}</dd>
        <dt>Area</dt><dd>{AREA_LABELS[d.area as keyof typeof AREA_LABELS] ?? d.area}</dd>
        <dt>Owner</dt><dd>{d.owner.name}</dd>
        <dt>Classification</dt><dd><span className={`cls cls-${d.classification}`}>{CLASSIFICATION_LABELS[d.classification] ?? d.classification}</span></dd>
        <dt>Visibility</dt><dd>{VISIBILITY_LABELS[d.visibility as keyof typeof VISIBILITY_LABELS] ?? d.visibility}</dd>
        <dt>Retention</dt><dd>{d.retention ? RETENTION_LABELS[d.retention] ?? d.retention : <span style={{ color: "var(--muted)" }}>Not set</span>}</dd>
        <dt>Effective</dt><dd>{d.approved_at ? fmtDT(d.approved_at) : fmt(d.effective_date)}</dd>
        <dt>Next review due</dt>
        <dd style={n !== null && d.revision ? (n < 0 ? { color: "var(--bad)", fontWeight: 600 } : n <= 30 ? { color: "var(--warn)", fontWeight: 600 } : undefined) : undefined}>
          {fmt(d.review_date)}{n !== null && d.revision ? (n < 0 ? " · overdue" : n <= 30 ? " · due soon" : "") : ""}
          {d.revision && (d.owner.id === me.user.id || p.adminCodes.includes(d.company)) && (
            <button className="linkbtn tiny" style={{ marginLeft: 8 }} title="You checked it and it's still right: the next review moves a full cycle on (ADR-0025)"
              onClick={() => { if (window.confirm(`Mark ${d.base_number} reviewed, with no changes? The next review moves a full cycle on.`)) void api.docReviewed(d.id).then(async (r) => { toast(`Marked reviewed. Next review ${fmt(r.next_review)}.`); await onSaved(); }, (e: Error) => toast(e.message)); }}>
              Reviewed, no changes</button>)}
        </dd>
        {d.revision && d.approved_by && <><dt>Approved by</dt><dd>{d.approved_by} · {fmtDT(d.approved_at)}{d.self_approved ? " · self-approved" : ""}</dd></>}
        <dt>Frameworks</dt>
        <dd>
          {!editing && (d.frameworks?.length ? <FrameworkTags tags={d.frameworks} /> : <span style={{ color: "var(--muted)" }}>None</span>)}
          {!editing && canTag && <button className="linkbtn tiny" style={{ marginLeft: 6 }} onClick={() => { setTags(d.frameworks ?? []); setEditing(true); }}>Edit</button>}
        </dd>
        <dt>Acknowledgment</dt>
        <dd>
          {data.permissions.can_set_ack
            ? <label style={{ display: "inline-flex", gap: 8, alignItems: "center", cursor: "pointer" }}>
                <input type="checkbox" checked={d.requires_ack} onChange={async (e) => {
                  const on = e.target.checked;
                  try { await api.setRequiresAck(d.id, on); toast(on ? "Readers will be asked to read and acknowledge it." : "Readers are no longer asked to acknowledge it."); await onSaved(); }
                  catch (err) { toast((err as Error).message); }
                }} />
                Readers must read and acknowledge it
              </label>
            : d.requires_ack ? "Readers must read and acknowledge it" : <span style={{ color: "var(--muted)" }}>Not required</span>}
          {d.requires_ack && (data.permissions.can_set_ack ? <AckAudience d={d} onSaved={onSaved} /> : <div className="cap">{(d.ack_roles ?? []).length ? `Asked of: ${(d.ack_roles ?? []).map((r) => ROLE_LABEL[r] ?? r).join(", ")}. ` : ""}{d.ack_each_version === false ? "One acknowledgment counts for every version." : "Asked again on each major or minor version; a revision fix keeps it."}</div>)}
        </dd>
      </dl>
      {data.permissions.can_edit_meta && <MetaEditor data={data} onSaved={onSaved} />}
      {d.classification === RESTRICTED && data.permissions.can_see_pending && <ReadersPanel data={data} canEdit={data.permissions.can_edit_meta} />}
      {editing && (
        <div className="fw-edit">
          <FrameworkPicker value={tags} onChange={setTags} disabled={saving} />
          <div className="help" style={{ margin: "6px 0" }}>Tags help people filter. Changing them doesn't need a new revision; the change is recorded in the audit log.</div>
          <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
            <button className="btn btn-ghost btn-sm" onClick={() => setEditing(false)} disabled={saving}>Cancel</button>
            <button className={`btn btn-accent btn-sm ${saving ? "busy" : ""}`} onClick={() => void saveTags()}>Save tags</button>
          </div>
        </div>
      )}
      <div className="note info" style={{ display: "block" }}>
        {isVideo ? <><b>Video:</b> everyone watches the approved video here. </> : d.type === "FRM" ? <><b>Form:</b> everyone can download the fillable original as well as the PDF. </> : "Readers get the approved PDF copy only. "}
        {canOriginalRole ? "As an author, reviewer, approver or admin you can also download the original file." : d.type === "FRM" || isVideo ? "" : "The original file is available to authors, reviewers, approvers and admins."}
      </div>
    </>
  );
}

// ---------------------------------------------------------------- ADR-0016 details
function MetaEditor({ data, onSaved }: { data: DocDetail; onSaved: () => Promise<void> }) {
  const { toast } = useStore();
  const d = data.document;
  const [open, setOpen] = useState(false);
  const [cls, setCls] = useState(d.classification);
  const [vis, setVis] = useState(d.visibility);
  const [ret, setRet] = useState(d.retention ?? "");
  const [owner, setOwner] = useState(d.owner.id);
  const [people, setPeople] = useState<{ id: string; name: string; roles: string[] }[]>([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) api.peopleIn(d.company).then((r) => setPeople(r.people), () => undefined); }, [open, d.company]);
  if (!open) return <button className="linkbtn tiny" style={{ margin: "2px 0 10px" }} onClick={() => setOpen(true)}>Edit classification, visibility, retention or owner</button>;
  // Who the server accepts as owner: author, approver or admin of the company
  // (company-wide documents: author or admin in any company).
  const ownerRoles = d.company === "CPY" ? ["author", "admin"] : ["author", "approver", "admin"];
  const staff = people.filter((x) => x.roles.some((r) => ownerRoles.includes(r)));
  const save = async () => {
    setBusy(true);
    try {
      await api.updateDoc(d.id, {
        ...(cls !== d.classification ? { classification: cls } : {}),
        ...(vis !== d.visibility ? { visibility: vis } : {}),
        ...(ret !== (d.retention ?? "") ? { retention: ret || null } : {}),
        ...(owner !== d.owner.id ? { owner_id: owner } : {}),
      });
      toast("Saved. The change is in the audit log."); setOpen(false); await onSaved();
    } catch (e) { toast((e as Error).message); } finally { setBusy(false); }
  };
  return (
    <div className="fw-edit meta-edit">
      <label><span className="lbl">Classification</span>
        <select className="inp" value={cls} onChange={(e) => setCls(e.target.value)}>
          {[...new Set([...CLASSIFICATION_CODES, d.classification])].map((k) => <option key={k} value={k}>{CLASSIFICATION_LABELS[k] ?? k}</option>)}
        </select></label>
      {cls === RESTRICTED && <div className="help">Restricted: only its authors, reviewers, approvers and admins, plus the people you name below, can read it.</div>}
      <label><span className="lbl">Visibility</span>
        <select className="inp" value={vis} onChange={(e) => setVis(e.target.value)}>
          {VISIBILITIES.map((k) => <option key={k} value={k}>{VISIBILITY_LABELS[k]}</option>)}
        </select></label>
      {vis !== "internal" && <div className="help">Client shared and Public take effect when client access arrives (Phase 3). Until then everyone outside your companies is still kept out.</div>}
      <label><span className="lbl">Retention</span>
        <select className="inp" value={ret} onChange={(e) => setRet(e.target.value)}>
          <option value="">Not set</option>
          {[...new Set([...RETENTION_CODES, ...(d.retention ? [d.retention] : [])])].map((k) => <option key={k} value={k}>{RETENTION_LABELS[k] ?? k}</option>)}
        </select></label>
      <label><span className="lbl">Owner</span>
        <select className="inp" value={owner} onChange={(e) => setOwner(e.target.value)}>
          {!staff.some((x) => x.id === d.owner.id) && <option value={d.owner.id}>{d.owner.name}</option>}
          {staff.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
        </select></label>
      <div style={{ display: "flex", gap: 6, justifyContent: "flex-end", marginTop: 8 }}>
        <button className="btn btn-ghost btn-sm" onClick={() => setOpen(false)} disabled={busy}>Cancel</button>
        <button className={`btn btn-accent btn-sm ${busy ? "busy" : ""}`} onClick={() => void save()}>Save</button>
      </div>
    </div>
  );
}

function ReadersPanel({ data, canEdit }: { data: DocDetail; canEdit: boolean }) {
  const { toast } = useStore();
  const d = data.document;
  const [readers, setReaders] = useState<{ id: string; name: string }[] | null>(null);
  const [people, setPeople] = useState<{ id: string; name: string; roles: string[] }[]>([]);
  const [pick, setPick] = useState("");
  const load = useCallback(() => api.readers(d.id).then((r) => setReaders(r.readers), () => setReaders([])), [d.id]);
  useEffect(() => { void load(); if (canEdit) api.peopleIn(d.company).then((r) => setPeople(r.people), () => undefined); }, [load, canEdit, d.company]);
  const save = async (ids: string[]) => {
    try { await api.setReaders(d.id, ids); await load(); toast("Readers updated. The change is in the audit log."); }
    catch (e) { toast((e as Error).message); }
  };
  // Anyone who belongs to the company can be named, except people who read it anyway
  // (authors, reviewers, approvers and admins; company-wide: admins, approvers and reviewers anywhere).
  const seesIt = d.company === "CPY" ? ["reviewer", "approver", "admin"] : ["author", "reviewer", "approver", "admin"];
  const candidates = people.filter((x) => !x.roles.some((r) => seesIt.includes(r)) && !readers?.some((r) => r.id === x.id));
  return (
    <div className="readers-panel">
      <div className="lbl">Who else can read it</div>
      <div className="help" style={{ marginTop: 0 }}>Restricted. Authors, reviewers, approvers and admins of {coName(d.company)} always can.</div>
      {readers === null ? <div className="cap">Loading…</div> : readers.length === 0 ? <div className="cap">Nobody else yet.</div> : (
        <ul className="reader-list">
          {readers.map((r) => <li key={r.id}>{r.name}{canEdit && <button className="linkbtn tiny" onClick={() => void save(readers.filter((x) => x.id !== r.id).map((x) => x.id))}>Remove</button>}</li>)}
        </ul>
      )}
      {canEdit && candidates.length > 0 && (
        <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
          <select className="inp" value={pick} onChange={(e) => setPick(e.target.value)} aria-label="Add a reader">
            <option value="">Add a person…</option>
            {candidates.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
          </select>
          <button className="btn btn-ghost btn-sm" disabled={!pick} onClick={() => { void save([...(readers ?? []).map((x) => x.id), pick]); setPick(""); }}>Add</button>
        </div>
      )}
    </div>
  );
}

function ArchiveForm({ busy, disabled, onCancel, onArchive, action = "Archive" }: { busy: boolean; disabled: boolean; onCancel: () => void; onArchive: (reason: string) => void; action?: string }) {
  const [reason, setReason] = useState("");
  return (
    <>
      <label className="lbl" htmlFor="arch-reason">Why (optional, shown on the document)</label>
      <input id="arch-reason" className="inp" maxLength={500} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Replaced by ITA-PLN-SEC-002" />
      <div className="row-btns" style={{ marginTop: 10 }}>
        <button className="btn btn-accent btn-sm" disabled={busy || disabled} onClick={() => onArchive(reason.trim())}>{action}</button>
        <button className="btn btn-ghost btn-sm" onClick={onCancel}>Cancel</button>
      </div>
    </>
  );
}

const ACT: Record<string, [string, string]> = {
  "document.create": ["created the draft", ""],
  "document.archive": ["archived the document", "bad"],
  "document.restore": ["restored the document", "ok"],
  "document.checkout": ["checked it out", ""],
  "document.checkin": ["checked it back in", ""],
  "document.retire": ["retired the document", "bad"],
  "document.renumber": ["renumbered it (new code lists)", ""],
  "document.meta": ["changed its classification or details", ""],
  "document.readers": ["changed who can read it", ""],
  "version.published": ["took effect", "ok"],
  "version.create": ["uploaded a new file", ""],
  "version.submit": ["submitted for approval", "warn"],
  "version.withdraw": ["withdrew the submission", ""],
  "version.changes_requested": ["requested changes", "bad"],
  "version.approve": ["approved", "ok"],
  "version.reviewed": ["reviewed it", "ok"],
  "version.approve_partial": ["approved (more approvers needed)", "ok"],
  "document.acknowledge": ["acknowledged it", "ok"],
  "document.ack_setting": ["changed whether it needs acknowledging", ""],
  "editor.save": ["saved changes in the browser editor", ""],
};

function ApprovalTab({ data, pendV, after, goEdit }: { data: DocDetail; pendV: VersionRow | null; after: (m: string) => Promise<void>; goEdit: () => void }) {
  const { toast } = useStore();
  const d = data.document, perm = data.permissions, pend = d.pending;
  const [cmt, setCmt] = useState("");
  const [attest, setAttest] = useState(false);
  const [busy, setBusy] = useState(false);
  // ADR-0016: the approver confirms the size of the change and may set a later effective date.
  const [bump, setBump] = useState<Bump>(pend?.bump ?? "minor");
  const [eff, setEff] = useState("");
  const act = async (fn: () => Promise<string>) => {
    setBusy(true);
    try { await after(await fn()); setCmt(""); setAttest(false); }
    catch (e) { toast((e as Error).message); }
    finally { setBusy(false); }
  };

  // This revision's story since the last approval (replaced uploads included, so a
  // change request stays visible after the author uploads a fixed file).
  const lastApproval = data.versions.filter((v) => v.state === "approved").map((v) => v.decided_at ?? "").sort().pop() ?? "";
  const log = pend
    ? data.timeline.filter((t) => ACT[t.action] && t.action !== "version.approve" && (!lastApproval || (parseTs(t.at)?.getTime() ?? 0) > (parseTs(lastApproval)?.getTime() ?? 0))).slice().reverse()
    : [];
  const waiting = pend?.state === "in_review"
    ? (perm.need_second_approver ? data.approvers.filter((a) => a.id !== pend.by.id) : data.approvers)
    : [];
  const versionsById = new Map(data.versions.map((v) => [v.id, v]));
  // Approvals of published versions only; partial approvals of the open revision show in its timeline.
  const record = data.approvals.filter((a) => a.decision === "approved" && versionsById.get(a.version_id)?.revision != null);
  const curV = d.version ? (d.version.split(".").map(Number) as [number, number, number]) : null;
  const nextV = fmtVersion(nextVersion(curV, curV ? bump : null))!;
  const todayIso = new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 10);
  const later = !!eff && eff > todayIso;
  const verb = later ? `Approve v${nextV} for ${fmt(eff)}` : `Approve & publish v${nextV}`;
  const approvalChoices = (
    <>
      {curV && <BumpPicker value={bump} onChange={setBump} current={d.version!} proposedBy={pend?.bump ? pend.by.name : null} />}
      <label className="lbl" htmlFor="eff" style={{ marginTop: 10 }}>Effective date <span style={{ fontWeight: 400, color: "var(--muted)" }}>(optional)</span></label>
      <input id="eff" className="inp" type="date" min={todayIso} value={eff} onChange={(e) => setEff(e.target.value)} style={{ maxWidth: 200 }} />
      <div className="help">Leave it blank to publish now. With a later date it shows as Approved{d.version ? ` and v${d.version} stays current` : ""} until then.</div>
    </>
  );

  const approve = () => {
    if (perm.self_approval) {
      if (!cmt.trim()) return toast("Say what you checked; it's required for self-approval.");
      if (!attest) return toast("Tick the reviewer statement to self-approve.");
    }
    void act(async () => {
      const r = await api.approve(pend!.version_id, cmt.trim(), attest, { bump: curV ? bump : undefined, effective_date: eff || undefined });
      return r.state === "scheduled"
        ? `${r.self_approved ? "Self-approved" : "Approved"} ${r.number}. It takes effect on ${fmt(r.effective_on)}.`
        : `${r.self_approved ? "Self-approved" : "Approved"}. Published as ${r.number}.`;
    });
  };
  const changes = () => {
    if (!cmt.trim()) { toast("Add a comment so the author knows what to change."); document.getElementById("cmt")?.focus(); return; }
    void act(async () => { await api.requestChanges(pend!.version_id, cmt.trim()); return "Changes requested and sent back to the author."; });
  };
  const withdraw = () => void act(async () => { await api.withdraw(pend!.version_id); return "Submission withdrawn. It's a draft again."; });

  return (
    <>
      {pend ? (
        <>
          <div className="eyebrow" style={{ marginBottom: 8 }}>v{pend.state === "in_review" ? nextV : pend.proposed_version} · {pend.state === "in_review" ? (pend.stage === "review" ? "awaiting review" : "awaiting approval") : pend.state === "changes_requested" ? "changes requested" : "draft"}</div>
          <ol className="timeline">
            {log.map((l, i) => {
              const [verb, cls] = ACT[l.action]!;
              const comment = typeof l.details?.comment === "string" ? l.details.comment : "";
              const resub = l.action === "version.submit" && l.details?.resubmitted === true;
              return (
                <li key={i}><i className={cls} /><div><span className="w">{l.actor_name ?? "Someone"}</span> {resub ? "resubmitted" : verb}</div><div className="d">{fmtDT(l.at)}</div>{comment && <div className="c">{comment}</div>}</li>
              );
            })}
            {pend.state === "in_review" && pend.stage === "review" && (
              <li><i /><div className="w" style={{ color: "var(--muted)" }}>Waiting for a review</div>
                <div className="d">A {coName(d.company)} rule asks for a reviewer to check it before it goes to an approver.</div></li>
            )}
            {pend.state === "in_review" && pend.stage !== "review" && (
              <li><i /><div className="w" style={{ color: "var(--muted)" }}>{pend.route.approvals > 1
                ? `Waiting on approvers: ${pend.approved_by.length} of ${pend.route.approvals}`
                : "Waiting on an approver"}</div>
                <div className="d">{pend.route.approvals > 1
                  ? `${pend.approved_by.length ? `Approved by ${pend.approved_by.join(", ")}. ` : ""}A ${coName(d.company)} rule asks for ${pend.route.approvals} different approvers, none of them the author.`
                  : waiting.map((a) => a.name + (a.id === pend.by.id ? " (author, self-approval allowed)" : "")).join(" or ") || "No approver - an admin needs to assign one"}
                  {pend.reviewed_by ? ` Reviewed by ${pend.reviewed_by}.` : ""}</div></li>
            )}
          </ol>
          {pendV && (
            <div className="note info" style={{ marginTop: 0, display: "block" }}>
              File: <b>{pendV.file_name}</b> · {bytes(pendV.file_size)}
              {pendV.sha256 && <><br /><span className="hash">sha256 {pendV.sha256.slice(0, 24)}…</span></>}
            </div>
          )}

          {perm.can_review && (
            <>
              <div className="note info" style={{ display: "block" }}><b>This needs a review before approval.</b> Check it, then mark it reviewed so it goes to an approver, or send it back with a comment.</div>
              <label className="lbl" htmlFor="cmt">Comment <span style={{ fontWeight: 400, color: "var(--muted)" }}>(required to request changes)</span></label>
              <textarea className="inp" id="cmt" rows={3} value={cmt} onChange={(e) => setCmt(e.target.value)} placeholder="What did you check? Anything to fix?" />
              <div className="row-btns">
                <button className={`btn btn-ok ${busy ? "busy" : ""}`} onClick={() => void act(async () => { await api.review(pend!.version_id, cmt.trim()); return "Marked reviewed. It's now waiting for an approver."; })}><Icon name="ok" />Mark reviewed</button>
                <button className={`btn btn-warn ${busy ? "busy" : ""}`} onClick={changes}>Request changes</button>
              </div>
            </>
          )}
          {perm.can_approve && perm.self_approval && (
            <>
              <div className="note warn" style={{ display: "block" }}><b>You're approving your own work.</b> {coName(d.company)} allows this because {perm.approver_count < 2 ? "you're its only approver" : "its admin hasn't required a second approver"}. The record will show <b>Self-approved</b>. An admin can turn on "Authors can't approve their own work" in Settings &gt; Reviews &amp; approvals.</div>
              {approvalChoices}
              <label className="lbl" htmlFor="cmt">What did you check? <span className="req">*</span></label>
              <textarea className="inp" id="cmt" rows={3} value={cmt} onChange={(e) => setCmt(e.target.value)} placeholder="Required for self-approval: what you reviewed before publishing" />
              <label style={{ display: "flex", gap: 8, alignItems: "flex-start", fontSize: 13, marginTop: 8 }}>
                <input type="checkbox" checked={attest} onChange={(e) => setAttest(e.target.checked)} style={{ marginTop: 3, accentColor: "var(--ink)" }} />
                <span>I reviewed this as the approver, separately from writing it.</span>
              </label>
              <div className="row-btns">
                <button className={`btn btn-ok ${busy ? "busy" : ""}`} onClick={approve}><Icon name="ok" />Self-{verb.charAt(0).toLowerCase() + verb.slice(1)}</button>
                <button className="btn btn-ghost" onClick={withdraw}>Withdraw</button>
              </div>
              <div className="help">{later ? "Schedules" : "Publishes"} <span className="mono">{d.base_number} v{nextV}</span> and records the approval as self-approved against this exact file.</div>
            </>
          )}
          {perm.can_approve && !perm.self_approval && (
            <>
              {approvalChoices}
              <label className="lbl" htmlFor="cmt">Comment <span style={{ fontWeight: 400, color: "var(--muted)" }}>(required to request changes)</span></label>
              <textarea className="inp" id="cmt" rows={3} value={cmt} onChange={(e) => setCmt(e.target.value)} placeholder="What did you check? Anything to fix?" />
              <div className="row-btns">
                <button className={`btn btn-ok ${busy ? "busy" : ""}`} onClick={approve}><Icon name="ok" />{verb}</button>
                <button className={`btn btn-warn ${busy ? "busy" : ""}`} onClick={changes}>Request changes</button>
              </div>
              <div className="help">Approving {later ? `schedules ${d.base_number} v${nextV} for ${fmt(eff)}` : `publishes ${d.base_number} v${nextV}`}, keeps the PDF copy and records your approval against this exact file.</div>
            </>
          )}
          {!perm.can_approve && pend.state === "in_review" && perm.is_uploader && (
            <>
              <div className="note warn">{pend.stage === "review"
                ? `You submitted this revision. A ${coName(d.company)} rule asks for a reviewer to check it first, then an approver signs off.`
                : pend.route.approvals > 1
                  ? `You submitted this revision. A ${coName(d.company)} rule asks for ${pend.route.approvals} approvers other than you.`
                  : `You submitted this revision and ${coName(d.company)} requires a second approver, so someone else signs off.`}</div>
              <div className="row-btns"><button className="btn btn-ghost" onClick={withdraw}>Withdraw submission</button></div>
            </>
          )}
          {pend.state !== "in_review" && perm.is_uploader && (
            pendV?.upload_confirmed_at
              ? <SubmitBox data={data} onDone={(m) => void after(m)} onEdit={goEdit} />
              : <div className="note warn" style={{ display: "block" }}>The file hasn't finished uploading. <button className="btn btn-ghost btn-sm" onClick={goEdit}>Upload it again</button></div>
          )}
          {pend.state === "in_review" && !perm.can_approve && !perm.can_review && !perm.is_uploader && (
            <div className="note info">{perm.approve_blocked_reason === "already_approved"
              ? `You've approved this revision. It needs ${pend.route.approvals - pend.approved_by.length} more approval${pend.route.approvals - pend.approved_by.length === 1 ? "" : "s"} from someone else.`
              : pend.stage === "review" ? "It's waiting for a review before approval." : `Only approvers for ${coName(d.company)} can sign off.`}</div>
          )}
        </>
      ) : (
        <div className="note ok">{d.scheduled ? `v${d.scheduled.version} is approved and takes effect on ${fmt(d.scheduled.effective_on)}.` : "No changes pending. The current version is approved."}</div>
      )}
      {record.length > 0 && (
        <>
          <div className="eyebrow" style={{ margin: "16px 0 8px" }}>Approval record</div>
          <ol className="timeline">
            {record.map((a, i) => {
              const v = versionsById.get(a.version_id);
              return (
                <li key={i}><i className="ok" />
                  <div><span className="w">v{v?.version ?? v?.revision ?? "?"}</span> approved by {a.approver_name}{a.self_approved && <> <span className="pill rev" style={{ fontSize: 10.5, padding: "1px 7px" }}>Self-approved</span></>}</div>
                  <div className="d">{fmtDT(a.decided_at)}{v ? ` · authored by ${v.uploader_name}` : ""}</div>
                  {a.comment && <div className="c">{a.comment}</div>}
                  {a.file_sha256 && <div className="hash">sha256 {a.file_sha256.slice(0, 24)}…</div>}
                </li>
              );
            })}
          </ol>
        </>
      )}
    </>
  );
}

function SubmitBox({ data, onDone, onEdit }: { data: DocDetail; onDone: (msg: string) => void; onEdit: () => void }) {
  const { me, toast } = useStore();
  const d = data.document, perm = data.permissions;
  const [to, setTo] = useState("");
  const [busy, setBusy] = useState(false);
  const [bump, setBump] = useState<Bump>(d.pending?.bump ?? "minor");
  const options = data.approvers.filter((a) => a.id !== me.user.id);
  const curV = d.version ? (d.version.split(".").map(Number) as [number, number, number]) : null;
  const nextV = fmtVersion(nextVersion(curV, curV ? bump : null));
  const submit = async () => {
    setBusy(true);
    try { await api.submit(d.pending!.version_id, to || undefined, false, curV ? bump : undefined); onDone(`Submitted ${d.base_number} v${nextV} for approval.`); }
    catch (e) { toast((e as Error).message); }
    finally { setBusy(false); }
  };
  return (
    <>
      {curV ? <BumpPicker value={bump} onChange={setBump} current={d.version!} proposedBy={null} />
        : <div className="note info" style={{ display: "block" }}>First release: approval makes it <b className="mono">v1.0.0</b>.</div>}
      <label className="lbl" htmlFor="apr" style={{ marginTop: 10 }}>Send to</label>
      <select className="inp" id="apr" value={to} onChange={(e) => setTo(e.target.value)}>
        <option value="">Any approver at {coName(d.company)}</option>
        {options.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
      </select>
      <div className="row-btns">
        <button className={`btn btn-accent ${busy ? "busy" : ""}`} onClick={submit}>Submit for approval</button>
        <button className="btn btn-ghost" onClick={onEdit}>Replace file</button>
      </div>
      {!perm.need_second_approver && (
        <div className="note info">{coName(d.company)} doesn't require a second approver{data.approvers.some((a) => a.id === me.user.id) ? ", so you can approve this yourself after submitting" : ""}.</div>
      )}
    </>
  );
}

/** Major / minor / revision, with the version it produces (ADR-0016). */
function BumpPicker({ value, onChange, current, proposedBy }: { value: Bump; onChange: (b: Bump) => void; current: string; proposedBy: string | null }) {
  const cur = current.split(".").map(Number) as [number, number, number];
  return (
    <fieldset className="bump">
      <legend className="lbl">Size of the change{proposedBy ? <span style={{ fontWeight: 400, color: "var(--muted)" }}> · {proposedBy} proposed {value}</span> : null}</legend>
      {BUMPS.map((b) => (
        <label key={b} className={`bump-opt ${value === b ? "on" : ""}`}>
          <input type="radio" name="bump" checked={value === b} onChange={() => onChange(b)} />
          <span><b className="mono">v{fmtVersion(nextVersion(cur, b))}</b> {BUMP_LABELS[b]}</span>
        </label>
      ))}
    </fieldset>
  );
}

function SubmitButton({ vid, onDone }: { vid: string; onDone: () => Promise<void> }) {
  const { toast } = useStore();
  const [busy, setBusy] = useState(false);
  return (
    <button className={`btn btn-accent ${busy ? "busy" : ""}`} onClick={async () => {
      setBusy(true);
      try { await api.submit(vid); await onDone(); } catch (e) { toast((e as Error).message); } finally { setBusy(false); }
    }}>Submit for approval</button>
  );
}

function RevisionsTab({ data }: { data: DocDetail }) {
  const { toast } = useStore();
  const d = data.document;
  const approved = data.versions.filter((v) => v.state === "approved" || v.state === "scheduled").sort((a, b) => (b.revision ?? 0) - (a.revision ?? 0));
  if (!approved.length) return <div className="note info">No approved versions yet. The first approval makes it v1.0.0.</div>;
  // Superseded versions stay available for audit: the download is stamped SUPERSEDED.
  const download = async (vid: string) => {
    try { window.location.assign((await api.fileUrl(d.id, { kind: "pdf", version: vid })).url); } catch (e) { toast((e as Error).message); }
  };
  return (
    <ol className="timeline">
      {approved.map((v) => {
        const tag = v.state === "scheduled" ? <span className="pill info" style={{ fontSize: 10.5, padding: "1px 7px" }}>from {fmt(v.effective_on)}</span>
          : v.revision === d.revision ? <span className="pill pub" style={{ fontSize: 10.5, padding: "1px 7px" }}>current</span>
          : <span className="pill draft" style={{ fontSize: 10.5, padding: "1px 7px" }}>superseded</span>;
        return (
          <li key={v.id}><i className={v.revision === d.revision ? "ok" : ""} />
            <div><span className="w mono">{d.base_number} v{v.version ?? `${v.revision}.0.0`}</span> {tag}</div>
            {v.notes && <div className="c">{v.notes}</div>}
            <div className="d">Approved {fmtDT(v.decided_at)}{v.effective_on && v.state !== "scheduled" ? ` · effective ${fmt(v.effective_on)}` : ""}{v.bump ? ` · ${v.bump} change` : ""}</div>
            <div className="d file">{v.file_name} · {bytes(v.file_size)}
              {data.permissions.can_see_pending && <button className="linkbtn tiny" style={{ marginLeft: 6 }} onClick={() => void download(v.id)}>PDF</button>}</div>
          </li>
        );
      })}
    </ol>
  );
}

function ActivityTab({ data }: { data: DocDetail }) {
  const versionsById = new Map(data.versions.map((v) => [v.id, v]));
  const rows = data.timeline.length
    ? data.timeline.filter((t) => ACT[t.action]).map((t) => {
        const v = t.version_id ? versionsById.get(t.version_id) : undefined;
        const rev = t.action === "version.approve" && typeof t.details?.version === "string" ? ` v${t.details.version}` : v?.version ? ` v${v.version}` : "";
        const verb = t.action === "version.approve" && t.details?.self_approved === true ? "self-approved" : ACT[t.action]![0];
        return { at: t.at, text: `${t.actor_name ?? "Someone"} ${verb}${rev}` };
      })
    : data.approvals.filter((a) => a.decision === "approved").map((a) => ({ at: a.decided_at, text: `v${versionsById.get(a.version_id)?.version ?? "?"} approved by ${a.approver_name}` }));
  if (!rows.length) return <Empty title="No activity yet" />;
  return (
    <>
      <ol className="timeline">
        {rows.map((r, i) => <li key={i}><i /><div>{r.text}</div><div className="d">{fmtDT(r.at)}</div></li>)}
      </ol>
      <div className="help">Every view, download and decision is in the audit log.</div>
    </>
  );
}

// ---------------------------------------------------------------- notes (2026-10-20)
/** Working notes on the draft and the On hold switch. Authors, approvers and admins only. */
function NotesTab({ data, canHold, after }: { data: DocDetail; canHold: boolean; after: () => Promise<void> }) {
  const { toast } = useStore();
  const d = data.document;
  const pend = d.pending;
  const [notes, setNotes] = useState<DocNote[] | null>(null);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.notes(d.id).then((r) => setNotes(r.notes), (e: Error) => toast(e.message)); }, [d.id, toast]);
  const add = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try { const r = await api.addNote(d.id, text.trim()); setNotes((n) => [r.note, ...(n ?? [])]); setText(""); await after(); }
    catch (e) { toast((e as Error).message); }
    finally { setBusy(false); }
  };
  const hold = async (on: boolean) => {
    setBusy(true);
    try { await api.setHold(d.id, on); toast(on ? "Put on hold. Submit is paused until you take it off hold." : "Taken off hold. It can be submitted again."); await after(); }
    catch (e) { toast((e as Error).message); }
    finally { setBusy(false); }
  };
  const canToggle = canHold && !!pend && pend.state !== "in_review";
  return (
    <div className="notes-tab">
      {pend && (
        <div className={`hold-box ${pend.on_hold ? "on" : ""}`}>
          <div>
            <b>{pend.on_hold ? "On hold" : "Not on hold"}</b>
            <span>{pend.on_hold ? "Parked while you wait on something. It can't be submitted until it's taken off hold." : "Waiting on information or another person? Put it on hold and add a note saying why."}</span>
          </div>
          {canToggle && <button className={`btn btn-sm ${pend.on_hold ? "btn-accent" : "btn-ghost"}`} disabled={busy} onClick={() => void hold(!pend.on_hold)}>{pend.on_hold ? "Take off hold" : "Put on hold"}</button>}
        </div>
      )}
      <label className="note-new"><span className="lbl">Add a note</span>
        <textarea className="inp" rows={3} value={text} maxLength={4000} onChange={(e) => setText(e.target.value)}
          placeholder="e.g. Waiting on Duc for the current printer models" onKeyDown={(e) => { if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) void add(); }} />
      </label>
      <div className="row-btns" style={{ justifyContent: "flex-end" }}>
        <button className="btn btn-accent btn-sm" disabled={busy || !text.trim()} onClick={() => void add()}>Add note</button>
      </div>
      <div className="help">Seen by authors, approvers and admins of {coName(d.company)}. Notes are never part of the revision history.</div>
      {!notes ? <Loading /> : notes.length === 0 ? <Empty title="No notes yet" /> : (
        <ol className="note-list">
          {notes.map((n) => (
            <li key={n.id}><div className="nb">{n.body}</div><div className="d">{n.author_name} · {fmtDT(n.created_at)}</div></li>
          ))}
        </ol>
      )}
    </div>
  );
}
